import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import FilmListRow from "@/components/feature/FilmListRow";
import usePageMeta from "@/hooks/usePageMeta";
import { FILMS, getCategoryName } from "@/mocks/films";

const SUGGESTIONS = ["巴黎", "爱情", "悬疑", "玛戈·德拉克洛瓦", "李奥·贝内特", "剧集", "文艺"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta({
    title: query
      ? `“${query}” 搜索结果 - 欧美成人电影`
      : "影片搜索 - 欧美成人电影",
    description: query
      ? `欧美成人电影站内搜索“${query}”的影片结果，包含影片封面、主演与剧情简介，支持按片名和演员姓名模糊匹配。`
      : "输入片名、英文片名或演员姓名，快速搜索欧美成人影视作品介绍与影片资料。",
    keywords: `欧美成人电影,影片搜索,${query || "影视资讯"}`,
    canonicalPath: "/search",
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return FILMS.filter((film) => {
      const haystack = [
        film.title,
        film.titleEn,
        film.director,
        ...film.cast,
        ...film.tags,
        ...film.categories.map((slug) => getCategoryName(slug)),
        film.region,
        String(film.year),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Search Archive"
        title="影片搜索"
        subtitle="支持按片名、英文片名、演员姓名、题材标签与地区进行模糊匹配，回车即可检索。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "搜索" }]}
      >
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row items-stretch gap-2 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-background-400" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入片名或演员名，如：巴黎、玛戈"
              aria-label="搜索影片关键词"
              className="w-full h-11 pl-10 pr-3 rounded-md border border-background-50/20 bg-background-50/10 text-sm text-background-50 placeholder:text-background-400 outline-none focus:border-accent-400 focus:bg-background-50/15"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-background-400">推荐关键词：</span>
          {SUGGESTIONS.map((item) => (
            <Link
              key={item}
              to={`/search?q=${encodeURIComponent(item)}`}
              className="px-2.5 py-1 rounded-full border border-background-50/20 text-[12px] text-background-200 hover:border-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
            >
              {item}
            </Link>
          ))}
        </div>
      </PageHero>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-background-200">
            <h2 className="font-heading text-lg font-semibold text-foreground-950">
              {query ? (
                <>
                  搜索关键词：<span className="text-accent-700">“{query}”</span>
                </>
              ) : (
                "请输入关键词开始搜索"
              )}
            </h2>
            <p className="text-[12px] text-foreground-500">
              共找到 <span className="text-accent-700 font-semibold">{results.length}</span> 条影片资料
            </p>
          </div>

          {query && results.length > 0 && (
            <div className="mt-5 flex flex-col gap-3">
              {results.map((film, index) => (
                <FilmListRow key={film.id} film={film} index={index} />
              ))}
            </div>
          )}

          {query && results.length === 0 && (
            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-2xl">
                <i className="ri-search-eye-line" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                没有找到与“{query}”相关的影片
              </h3>
              <p className="mt-2 text-[13px] text-foreground-500">
                可以尝试更短的片名关键词、演员姓名，或直接浏览分类页面。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setSearchParams({})}
                  className="h-10 px-5 rounded-md bg-primary-900 text-background-50 text-sm whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer"
                >
                  清空搜索词
                </button>
                <Link
                  to="/"
                  className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm flex items-center whitespace-nowrap hover:border-accent-400 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}

          {!query && (
            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 px-6 py-14 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-2xl">
                <i className="ri-search-line" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                输入关键词开始检索
              </h3>
              <p className="mt-2 text-[13px] text-foreground-500">
                例如片名「巴黎午夜叙事」，或演员「伊莎贝尔·罗西」。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.slice(0, 4).map((item) => (
                  <Link
                    key={item}
                    to={`/search?q=${encodeURIComponent(item)}`}
                    className="px-3 py-1.5 rounded-md border border-background-300 text-[12px] text-foreground-700 hover:border-accent-400 transition-colors"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}